// PDP buy button: "Add to bag" adds the variant without opening the mini cart,
// then turns into a quantity control ("N in bag") for that variant.
// Discounts (e.g. "3 for 24€") can split one variant over several cart lines,
// so the count is always the sum of the variant's lines: "+" adds units,
// "−" lowers one specific line, and the cart is re-read after every change.
if (!customElements.get('wd-buy-button')) {
  customElements.define(
    'wd-buy-button',
    class WdBuyButton extends HTMLElement {
      connectedCallback() {
        this.miniCart = document.querySelector('mini-cart');
        this.lines = null;

        this.addEventListener('submit', this.onSubmit.bind(this));
        this.addEventListener('click', this.onClick.bind(this));

        this.unsubscribers = [
          subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this)),
          subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this)),
        ];

        this.render();

        // Buttons rendered without cart data (React carousel cards) read their count from the cart
        if (this.hasAttribute('data-sync-cart')) this.syncFromSharedCart();
      }

      // One /cart.js request for every button that mounts at the same time (a carousel renders many)
      syncFromSharedCart() {
        if (!WdBuyButton.sharedCart) {
          WdBuyButton.sharedCart = this.fetchCart().finally(() => {
            WdBuyButton.sharedCart = null;
          });
        }
        WdBuyButton.sharedCart.then((cart) => this.syncFromCart(cart)).catch((e) => console.error(e));
      }

      disconnectedCallback() {
        this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
      }

      get quantity() {
        return parseInt(this.dataset.quantity, 10) || 0;
      }

      set quantity(value) {
        this.dataset.quantity = Math.max(0, value);
        this.render();
      }

      get step() {
        return parseInt(this.dataset.step, 10) || 1;
      }

      get max() {
        return this.dataset.max ? parseInt(this.dataset.max, 10) : null;
      }

      get variantId() {
        return this.querySelector('input[name="id"]')?.value || this.dataset.variantId;
      }

      onSubmit(event) {
        event.preventDefault();
        const form = event.target;
        if (this.busy || form.querySelector('[name="add"]')?.disabled) return;
        if (!this.requiredFieldsFilled(form)) return;

        const formData = new FormData(form);
        this.appendSections(formData);

        this.run(() => this.post(theme.routes.cart_add_url, formData));
      }

      onClick(event) {
        // No-form mode (inside the cart drawer's form): the Add to bag button is type="button"
        const addButton = event.target.closest('.wd-buy-button__add[type="button"]');
        if (addButton) {
          if (!this.busy && !addButton.disabled) this.run(() => this.increase());
          return;
        }

        const button = event.target.closest('.wd-buy-button__step');
        if (!button || this.busy) return;

        this.run(() => (button.name === 'plus' ? this.increase() : this.decrease()));
      }

      increase() {
        return this.post(
          theme.routes.cart_add_url,
          JSON.stringify({ items: [{ id: Number(this.variantId), quantity: this.step }], ...this.sectionsParams() })
        );
      }

      async decrease() {
        if (!this.lines) this.syncFromCart(await this.fetchCart());
        const line = this.lines[this.lines.length - 1];
        if (!line) return null;

        return this.post(
          theme.routes.cart_change_url,
          JSON.stringify({ id: line.key, quantity: Math.max(0, line.quantity - this.step), ...this.sectionsParams() })
        );
      }

      // Runs one cart change at a time, then re-reads the cart so the count matches every line
      async run(change) {
        this.setBusy(true);
        this.showError(false);
        try {
          const response = await change();
          if (!response) return;
          const cart = await this.fetchCart();
          this.syncFromCart(cart);
          this.afterCartChange(response, cart);
        } catch (error) {
          if (error?.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'wd-buy-button',
              productVariantId: this.variantId,
              errors: error.errors || error.description,
              message: error.message,
            });
            this.showError(error.description || error.message);
          } else {
            console.error(error);
          }
        } finally {
          this.setBusy(false);
        }
      }

      async post(url, body) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        if (body instanceof FormData) delete config.headers['Content-Type'];
        config.body = body;

        const response = await (await fetch(url, config)).json();
        if (response.status) throw response;
        return response;
      }

      async fetchCart() {
        return (await fetch(`${theme.routes.cart_url}.js`)).json();
      }

      // Refresh the mini cart and the header bubble, without opening the drawer
      afterCartChange(response, cart) {
        if (this.miniCart && response.sections) {
          this.miniCart.renderContents(response, { open: false });
          this.miniCart.classList.toggle('is-empty', cart.item_count === 0);
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'wd-buy-button',
          productVariantId: this.variantId,
          cartData: cart,
        });
      }

      sectionsParams() {
        if (!this.miniCart) return {};
        return {
          sections: this.miniCart.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        };
      }

      appendSections(formData) {
        Object.entries(this.sectionsParams()).forEach(([key, value]) => formData.append(key, value));
      }

      syncFromCart(cart) {
        this.lines = (cart.items || [])
          .filter((item) => String(item.variant_id) === String(this.variantId))
          .map(({ key, quantity }) => ({ key, quantity }));
        this.quantity = this.lines.reduce((total, line) => total + line.quantity, 0);
      }

      // Cart changed elsewhere (mini cart, cart page) – keep the count in sync
      onCartUpdate(event) {
        if (event?.source === 'wd-buy-button') return;
        this.fetchCart()
          .then((cart) => this.syncFromCart(cart))
          .catch((e) => console.error(e));
      }

      onVariantChange(event) {
        if (event.data.sectionId !== this.dataset.sectionId) return;
        const updated = event.data.html.querySelector('wd-buy-button');
        if (!updated) return;

        ['variantId', 'quantity', 'min', 'step', 'max'].forEach((key) => {
          if (updated.dataset[key] !== undefined) this.dataset[key] = updated.dataset[key];
          else delete this.dataset[key];
        });
        this.innerHTML = updated.innerHTML;
        this.lines = null;
        this.render();
      }

      render() {
        const quantity = this.quantity;
        const addButton = this.querySelector('.wd-buy-button__add');
        const quantityControl = this.querySelector('.wd-buy-button__quantity');
        if (!addButton || !quantityControl) return;

        addButton.hidden = quantity > 0;
        quantityControl.hidden = quantity === 0;
        quantityControl.classList.toggle('is-last', quantity <= this.step);

        const count = this.querySelector('.wd-buy-button__count');
        if (count) count.textContent = this.dataset.inBagTemplate.replace('[quantity]', quantity);

        const plus = quantityControl.querySelector('[name="plus"]');
        if (plus) plus.disabled = this.max !== null && quantity + this.step > this.max;
      }

      setBusy(busy) {
        this.busy = busy;
        this.classList.toggle('is-loading', busy);
        this.setAttribute('aria-busy', busy);
      }

      showError(message = false) {
        const wrapper = this.querySelector('.wd-buy-button__error');
        if (!wrapper) return;
        wrapper.hidden = !message;
        if (message) wrapper.querySelector('.wd-buy-button__error-message').textContent = message;
      }

      // Line item property fields live outside the form and point to it with form=""
      requiredFieldsFilled(form) {
        const fields = Array.from(document.querySelectorAll('[required]')).filter(
          (field) => field.getAttribute('form') === form.id || field.closest('form') === form
        );
        let allFilled = true;
        fields.forEach((field) => {
          const valid =
            field.type === 'checkbox' || field.type === 'radio'
              ? Array.from(document.querySelectorAll(`input[name="${field.name}"]`)).some((input) => input.checked)
              : field.value.trim() !== '';
          field.classList.toggle('invalid', !valid);
          if (!valid) allFilled = false;
        });
        if (!allFilled) this.showError(theme.accessibilityStrings.fillInAllLineItemPropertyRequiredFields);
        return allFilled;
      }
    }
  );
}
